import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/auth/AuthProvider';
import {getImageUrl} from '@/lib/utils';
import { useTenant } from '@/contexts/TenantContext';
import { useLeadInsertNotifications } from '@/hooks/useLeadInsertNotifications';
import {
  LayoutDashboard,
  FileSpreadsheet,
  HardDrive,
  Link as LinkIcon,
  LogOut,
  Menu,
  X,
  Database,
  Users,
  BarChart3,
  Building2,
  FileText
} from 'lucide-react';

const navigation = [
  {
    name: 'Dashboard',
    href: '/system/dashboard',
    icon: LayoutDashboard,
    description: 'Overview and system health'
  },
  {
    name: 'Tenant Management',
    href: '/system/tenants',
    icon: Building2,
    description: 'Manage tenant instances and configurations'
  },
  {
    name: 'Metrics',
    href: '/system/metrics',
    icon: BarChart3,
    description: 'Analytics and performance insights'
  },
  {
    name: 'Database Health',
    href: '/system/database',
    icon: Database,
    description: 'Connection tests and metrics'
  },
  {
    name: 'Excel Exports',
    href: '/system/exports',
    icon: FileSpreadsheet,
    description: 'Export data to Excel files'
  },
  {
    name: 'File Management',
    href: '/system/files',
    icon: HardDrive,
    description: 'Manage uploaded files'
  },
  {
    name: 'Submission Tracker',
    href: '/system/submissions',
    icon: LinkIcon,
    description: 'Track submissions and files'
  },
  {
    name: 'All Leads',
    href: '/system/leads',
    icon: FileSpreadsheet,
    description: 'All leads from all tenant instances'
  },
  {
    name: 'Blog CMS',
    href: '/system/blog',
    icon: FileText,
    description: 'Manage blog posts'
  },

  {
    name: 'Tenant Dashboard - Audits',
    href: '/system/tenant-audits',
    icon: FileSpreadsheet,
    description: 'Audits tenant submissions (Excel-like view)'
  },
  {
    name: 'Tenant Dashboard - Dubai CommerCity',
    href: '/system/tenant-dcc',
    icon: FileSpreadsheet,
    description: 'Dubai CommerCity tenant submissions (Excel-like view)'
  },
  {
    name: 'Tenant Dashboard - Incorpify',
    href: '/system/tenant-incorpify',
    icon: FileSpreadsheet,
    description: 'Incorpify tenant submissions (Excel-like view)'
  },
  {
    name: 'Tenant Dashboard - Virtuzone',
    href: '/system/tenant-virtuzone',
    icon: FileSpreadsheet,
    description: 'Virtuzone tenant submissions (Excel-like view)'
  }
];

export function AdminLayout() {
  const tenantContext = useTenant();
  const t = tenantContext?.tenant ?? null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Live lead notifications (all tenants)
  // We show across the admin suite; no tenant filter
  useLeadInsertNotifications();

  const handleSignOut = async () => {
    await signOut();
    navigate('/system/access');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col min-h-0 bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <Link to="/" className="flex items-center">
              {/* Desktop logo */}
              <img
                src="/Logos/new/newlogo.png"
                alt={t?.name || "Yallacompare for Business"}
                className="h-8 w-auto object-contain hidden sm:block"
              />
              {/* Mobile logo */}
              <img
                src="/Logos/new/newlogo.png"
                alt={t?.name || "Yallacompare for Business"}
                className="h-8 w-auto object-contain sm:hidden"
              />

            </Link>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="flex-1 overflow-y-auto space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow min-h-0 bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4 border-b border-gray-200">
            <Link to="/" className="flex items-center">
              {/* Desktop logo */}
              <img
                src={getImageUrl("yallacompare-for-business-logo.png")}
                alt="Yallacompare for Business"
                className="h-8 w-auto object-contain hidden sm:block"
              />
              {/* Mobile logo */}
              <img
                src={getImageUrl("yallacompare-for-business-logo.png")}
                alt="Yallacompare for Business"
                className="h-8 w-auto object-contain sm:hidden"
              />

            </Link>
          </div>
          <nav className="flex-1 overflow-y-auto space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  <div>
                    <div>{item.name}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </Link>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.email}
                </p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 bg-white border-b border-gray-200 lg:hidden">
          <Button
            variant="ghost"
            size="sm"
            className="px-4"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1 flex items-center justify-between px-4">
            <h1 className="text-lg font-semibold">Admin Dashboard</h1>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
